/**
 * Healthcare Revenue Cycle Management (RCM) Example Workflows
 *
 * Domain-agnostic, data-driven workflow examples using the 16 primitive types.
 * Demonstrates complete RCM process from patient registration to payment posting.
 *
 * Life-critical: Full audit trails, compliance tracking, HIPAA compliance.
 * These are EXAMPLE workflows that can be loaded into the designer.
 */

import { WorkflowMetadata } from '../shared/WorkflowManagementSidebar';

/**
 * Example 1: Patient Registration & Eligibility Verification
 *
 * Workflow Type Composition:
 * - WorkflowDefinition: Main registration flow
 * - ActorDescriptor: Registration clerk, insurance verifier
 * - Task: Data entry, verification tasks
 * - Integration: Insurance payer systems (Availity, Change Healthcare)
 * - Policy: HIPAA compliance, data validation
 * - ExecutionResult: Verification outcome
 */
export const PATIENT_REGISTRATION_WORKFLOW: WorkflowMetadata = {
  id: 'rcm_patient_registration_001',
  name: 'Patient Registration & Eligibility Verification',
  description:
    'Complete patient intake process with real-time insurance eligibility verification and HIPAA-compliant data collection',
  version: '2.1.0',
  status: 'active',
  createdAt: '2025-09-01T08:00:00Z',
  createdBy: 'system',
  updatedAt: '2025-09-30T10:00:00Z',
  updatedBy: 'rcm_admin',
  complianceFlags: ['HIPAA', 'HITECH', 'STATE_INSURANCE_REGS'],
  tags: ['rcm', 'registration', 'eligibility', 'patient-access'],
  tenant: 'healthcare_org',
  nodes: [
    {
      id: 'wf_def_registration',
      type: 'WorkflowDefinition',
      name: 'Registration Workflow',
      x: 150,
      y: 250,
      metadata: {
        name: 'Patient Registration & Eligibility Verification',
        description: 'RCM front-end workflow for patient access',
        version: '2.1.0',
        startAt: 'collect_demographics',
        timeout: 1800,
        metadata: {
          rcm_phase: 'patient_access',
          criticality: 'high',
          average_duration_minutes: 15,
        },
      },
    },
    {
      id: 'task_collect_demographics',
      type: 'Task',
      name: 'Collect Demographics',
      x: 400,
      y: 200,
      metadata: {
        taskId: 'task_demographics_001',
        taskType: 'manual',
        name: 'Collect Patient Demographics',
        assignee: 'Registration Clerk',
        priority: 'high',
        fields: [
          'patient_name',
          'date_of_birth',
          'ssn',
          'address',
          'phone',
          'email',
          'emergency_contact',
        ],
        validation: {
          required: ['patient_name', 'date_of_birth', 'ssn'],
          hipaa_consent: true,
        },
      },
    },
    {
      id: 'policy_hipaa_validation',
      type: 'Policy',
      name: 'HIPAA Validation',
      x: 400,
      y: 350,
      metadata: {
        policyId: 'policy_hipaa_registration_001',
        policyType: 'compliance',
        rules: {
          hipaa_consent_required: true,
          phi_encryption_required: true,
          audit_log_all_access: true,
          ssn_masking: true,
        },
        severity: 'critical',
      },
    },
    {
      id: 'task_collect_insurance',
      type: 'Task',
      name: 'Collect Insurance',
      x: 650,
      y: 250,
      metadata: {
        taskId: 'task_insurance_001',
        taskType: 'manual',
        name: 'Collect Insurance Information',
        assignee: 'Registration Clerk',
        priority: 'high',
        fields: [
          'primary_insurance_name',
          'policy_number',
          'group_number',
          'subscriber_name',
          'subscriber_relationship',
          'secondary_insurance',
        ],
      },
    },
    {
      id: 'integration_eligibility_check',
      type: 'Integration',
      name: 'Eligibility API',
      x: 900,
      y: 250,
      metadata: {
        integrationId: 'int_availity_eligibility_001',
        provider: 'availity',
        name: 'Availity Eligibility API',
        endpoint: 'https://api.availity.com/v1/eligibility',
        authConfig: {
          type: 'oauth2',
          clientId: '${AVAILITY_CLIENT_ID}',
          scope: 'eligibility',
        },
        requestMapping: {
          memberId: '$.patient.insuranceNumber',
          payerId: '$.insurance.payerId',
          serviceDate: '$.appointment.date',
        },
      },
    },
    {
      id: 'result_eligibility_outcome',
      type: 'ExecutionResult',
      name: 'Eligibility Result',
      x: 1150,
      y: 200,
      metadata: {
        resultId: 'result_eligibility_001',
        instanceId: '${workflow.instanceId}',
        status: 'success',
        output: {
          eligible: true,
          coverage_active: true,
          copay_amount: 25.0,
          deductible_remaining: 500.0,
          out_of_pocket_max: 2000.0,
        },
      },
    },
    {
      id: 'escalation_eligibility_failure',
      type: 'Escalation',
      name: 'Escalate Failure',
      x: 1150,
      y: 350,
      metadata: {
        escalationId: 'esc_eligibility_failure_001',
        severity: 'high',
        reason: 'Insurance eligibility verification failed',
        escalateTo: 'role_insurance_verifier',
        deadline: '+4h',
      },
    },
  ],
  connections: [
    // Clean execution flow (actors shown as badges on tasks, not separate nodes)
    { source: 'wf_def_registration', target: 'task_collect_demographics' },
    { source: 'task_collect_demographics', target: 'policy_hipaa_validation' },
    { source: 'policy_hipaa_validation', target: 'task_collect_insurance' },
    { source: 'task_collect_insurance', target: 'integration_eligibility_check' },
    { source: 'integration_eligibility_check', target: 'result_eligibility_outcome' },
    { source: 'integration_eligibility_check', target: 'escalation_eligibility_failure' },
  ],
};

/**
 * Example 2: Medical Coding & Charge Capture
 *
 * Workflow Type Composition:
 * - WorkflowDefinition: Coding workflow
 * - Task: Code assignment, charge entry
 * - ActorDescriptor: Certified coder, charge auditor
 * - Policy: Coding compliance (ICD-10, CPT)
 * - Integration: EMR (Epic, Cerner), scrubbing tools
 * - OptimizationRecommendation: AI-suggested codes
 */
export const MEDICAL_CODING_WORKFLOW: WorkflowMetadata = {
  id: 'rcm_medical_coding_002',
  name: 'Medical Coding & Charge Capture',
  description:
    'Automated medical coding with AI assistance, compliance validation, and charge capture for accurate billing',
  version: '3.0.0',
  status: 'active',
  createdAt: '2025-08-15T08:00:00Z',
  createdBy: 'system',
  updatedAt: '2025-09-30T10:00:00Z',
  updatedBy: 'coding_manager',
  complianceFlags: ['HIPAA', 'CODING_COMPLIANCE', 'FRAUD_PREVENTION'],
  tags: ['rcm', 'coding', 'charge-capture', 'hcc', 'compliance'],
  tenant: 'healthcare_org',
  nodes: [
    {
      id: 'wf_def_coding',
      type: 'WorkflowDefinition',
      name: 'Medical Coding Workflow',
      x: 100,
      y: 250,
      metadata: {
        name: 'Medical Coding & Charge Capture',
        version: '3.0.0',
        startAt: 'fetch_clinical_documentation',
        timeout: 7200,
      },
    },
    {
      id: 'integration_emr_fetch',
      type: 'Integration',
      name: 'Fetch from EMR',
      x: 350,
      y: 200,
      metadata: {
        integrationId: 'int_epic_clinical_data_001',
        provider: 'epic',
        name: 'Epic FHIR API',
        endpoint: 'https://epic.hospital.org/fhir/r4',
        authConfig: {
          type: 'oauth2',
          scope: 'patient/*.read encounter/*.read',
        },
      },
    },
    {
      id: 'ai_code_suggestions',
      type: 'OptimizationRecommendation',
      name: 'AI Code Suggestions',
      x: 600,
      y: 150,
      metadata: {
        recommendationId: 'ai_coding_001',
        recommendationType: 'ai_coding',
        description: 'ML-powered ICD-10/CPT code suggestions based on clinical documentation',
        impact: '40% faster coding, 15% higher specificity',
        savings: '$250K annually',
        confidence: 92,
      },
    },
    {
      id: 'task_code_assignment',
      type: 'Task',
      name: 'Assign Codes',
      x: 850,
      y: 200,
      metadata: {
        taskId: 'task_code_assignment_001',
        taskType: 'manual',
        name: 'Assign ICD-10/CPT Codes',
        assignee: 'Certified Coder',
        priority: 'high',
        fields: ['icd10_codes', 'cpt_codes', 'modifiers', 'units'],
      },
    },
    {
      id: 'policy_coding_compliance',
      type: 'Policy',
      name: 'Coding Compliance Check',
      x: 1100,
      y: 250,
      metadata: {
        policyId: 'policy_coding_compliance_001',
        policyType: 'compliance',
        rules: {
          icd10_specificity_required: true,
          medicare_lcd_compliance: true,
          unbundling_prevention: true,
          medical_necessity_documented: true,
        },
        severity: 'critical',
      },
    },
    {
      id: 'task_charge_entry',
      type: 'Task',
      name: 'Enter Charges',
      x: 1350,
      y: 200,
      metadata: {
        taskId: 'task_charge_entry_001',
        taskType: 'automated',
        priority: 'medium',
      },
    },
  ],
  connections: [
    // Clean execution flow (actors shown as badges on tasks)
    { source: 'wf_def_coding', target: 'integration_emr_fetch' },
    { source: 'integration_emr_fetch', target: 'ai_code_suggestions' },
    { source: 'ai_code_suggestions', target: 'task_code_assignment' },
    { source: 'task_code_assignment', target: 'policy_coding_compliance' },
    { source: 'policy_coding_compliance', target: 'task_charge_entry' },
  ],
};

/**
 * Example 3: Claims Submission & Adjudication
 *
 * Workflow Type Composition:
 * - WorkflowDefinition: Claims processing
 * - Task: Claim scrubbing, submission
 * - Integration: Clearinghouse (Change Healthcare, Waystar)
 * - Schedule: Batch submission schedules
 * - Instance: Live claim tracking
 * - Signal: Claim status updates, remittance advice
 * - JobStatus: Submission batch status
 */
export const CLAIMS_SUBMISSION_WORKFLOW: WorkflowMetadata = {
  id: 'rcm_claims_submission_003',
  name: 'Claims Submission & Adjudication',
  description:
    'Automated claims scrubbing, clearinghouse submission, and real-time adjudication tracking',
  version: '4.2.1',
  status: 'active',
  createdAt: '2025-07-01T08:00:00Z',
  createdBy: 'system',
  updatedAt: '2025-09-30T10:00:00Z',
  updatedBy: 'billing_director',
  complianceFlags: ['HIPAA', 'X12_COMPLIANCE', 'FRAUD_PREVENTION'],
  tags: ['rcm', 'claims', 'submission', 'adjudication', 'clearinghouse'],
  tenant: 'healthcare_org',
  nodes: [
    {
      id: 'wf_def_claims',
      type: 'WorkflowDefinition',
      name: 'Claims Submission Workflow',
      x: 100,
      y: 300,
      metadata: {
        name: 'Claims Submission & Adjudication',
        version: '4.2.1',
        startAt: 'scrub_claim',
        timeout: 14400,
      },
    },
    {
      id: 'task_scrub_claim',
      type: 'Task',
      name: 'Claim Scrubbing',
      x: 350,
      y: 250,
      metadata: {
        taskId: 'task_scrub_001',
        taskType: 'automated',
        priority: 'high',
        validation: [
          'npi_valid',
          'diagnosis_codes_valid',
          'modifiers_appropriate',
          'medical_necessity',
        ],
      },
    },
    {
      id: 'policy_submission_rules',
      type: 'Policy',
      name: 'Payer Submission Rules',
      x: 600,
      y: 300,
      metadata: {
        policyId: 'policy_payer_rules_001',
        policyType: 'business',
        rules: {
          medicare_timely_filing: '365_days',
          medicaid_timely_filing: '180_days',
          commercial_timely_filing: '90_days',
          prior_auth_required_for: ['inpatient', 'surgery', 'advanced_imaging'],
        },
        severity: 'critical',
      },
    },
    {
      id: 'schedule_batch_submission',
      type: 'Schedule',
      name: 'Daily Claims Batch',
      x: 600,
      y: 450,
      metadata: {
        scheduleId: 'schedule_claims_batch_001',
        scheduleType: 'cron',
        expression: '0 1,13 * * *',
        timezone: 'America/New_York',
        enabled: true,
        description: 'Submit claims twice daily at 1 AM and 1 PM EST',
      },
    },
    {
      id: 'integration_clearinghouse',
      type: 'Integration',
      name: 'Submit to Clearinghouse',
      x: 850,
      y: 250,
      metadata: {
        integrationId: 'int_change_healthcare_001',
        provider: 'change_healthcare',
        name: 'Change Healthcare Clearinghouse',
        endpoint: 'https://api.changehealthcare.com/v1/claims',
        authConfig: {
          type: 'api_key',
          header: 'X-API-Key',
        },
      },
    },
    {
      id: 'instance_claim_tracking',
      type: 'Instance',
      name: 'Claim Instance Tracking',
      x: 1100,
      y: 200,
      metadata: {
        instanceId: '${claim.id}',
        workflowId: 'wf_def_claims',
        status: 'running',
        input: {
          claimId: '${claim.id}',
          patientId: '${patient.id}',
          serviceDate: '${encounter.date}',
        },
        currentState: 'submitted',
      },
    },
    {
      id: 'signal_remittance_advice',
      type: 'Signal',
      name: 'ERA Received Signal',
      x: 1350,
      y: 250,
      metadata: {
        signalId: 'signal_era_001',
        signalType: 'webhook',
        name: 'Electronic Remittance Advice Webhook',
        payload: {
          claim_status: 'paid | denied | pending',
          paid_amount: 'number',
          adjustments: 'array',
        },
      },
    },
    {
      id: 'job_status_batch',
      type: 'JobStatus',
      name: 'Batch Submission Status',
      x: 1100,
      y: 400,
      metadata: {
        jobId: 'job_batch_${date}',
        status: 'processing',
        progress: 75,
        message: 'Processing 234 of 312 claims',
      },
    },
  ],
  connections: [
    { source: 'wf_def_claims', target: 'task_scrub_claim' },
    { source: 'task_scrub_claim', target: 'policy_submission_rules' },
    { source: 'policy_submission_rules', target: 'integration_clearinghouse' },
    { source: 'schedule_batch_submission', target: 'integration_clearinghouse' },
    { source: 'integration_clearinghouse', target: 'instance_claim_tracking' },
    { source: 'instance_claim_tracking', target: 'signal_remittance_advice' },
    { source: 'integration_clearinghouse', target: 'job_status_batch' },
  ],
};

/**
 * Example 4: Payment Posting & Reconciliation
 *
 * Workflow Type Composition:
 * - WorkflowDefinition: Payment posting process
 * - Task: Post payments, handle denials
 * - Signal: ERA/EOB received
 * - ExecutionResult: Posting outcome
 * - Escalation: Denial management
 * - TimelineItem: Payment history tracking
 */
export const PAYMENT_POSTING_WORKFLOW: WorkflowMetadata = {
  id: 'rcm_payment_posting_004',
  name: 'Payment Posting & Reconciliation',
  description:
    'Automated payment posting from ERAs with denial management and accounts receivable reconciliation',
  version: '1.8.0',
  status: 'active',
  createdAt: '2025-06-01T08:00:00Z',
  createdBy: 'system',
  updatedAt: '2025-09-30T10:00:00Z',
  updatedBy: 'ar_manager',
  complianceFlags: ['HIPAA', 'FINANCIAL_COMPLIANCE', 'SOX'],
  tags: ['rcm', 'payment-posting', 'ar', 'denial-management'],
  tenant: 'healthcare_org',
  nodes: [
    {
      id: 'wf_def_payment_posting',
      type: 'WorkflowDefinition',
      name: 'Payment Posting Workflow',
      x: 100,
      y: 300,
      metadata: {
        name: 'Payment Posting & Reconciliation',
        version: '1.8.0',
        startAt: 'receive_remittance',
      },
    },
    {
      id: 'signal_era_received',
      type: 'Signal',
      name: 'ERA/EOB Received',
      x: 350,
      y: 250,
      metadata: {
        signalId: 'signal_era_received_001',
        signalType: 'webhook',
        name: 'Electronic Remittance Advice',
      },
    },
    {
      id: 'task_auto_post_payment',
      type: 'Task',
      name: 'Auto-Post Payments',
      x: 600,
      y: 200,
      metadata: {
        taskId: 'task_auto_post_001',
        taskType: 'automated',
        priority: 'high',
      },
    },
    {
      id: 'task_denial_handling',
      type: 'Task',
      name: 'Handle Denials',
      x: 600,
      y: 350,
      metadata: {
        taskId: 'task_denial_001',
        taskType: 'manual',
        assignee: 'role_denial_specialist',
        priority: 'critical',
      },
    },
    {
      id: 'escalation_underpayment',
      type: 'Escalation',
      name: 'Escalate Underpayment',
      x: 850,
      y: 400,
      metadata: {
        escalationId: 'esc_underpayment_001',
        severity: 'medium',
        reason: 'Payment less than expected reimbursement',
        escalateTo: 'role_ar_manager',
      },
    },
    {
      id: 'timeline_payment_history',
      type: 'TimelineItem',
      name: 'Payment History Entry',
      x: 850,
      y: 200,
      metadata: {
        timelineId: 'timeline_payment_${claim.id}',
        timestamp: '${payment.received_at}',
        eventType: 'payment_posted',
        actor: 'system',
        details: {
          amount_paid: '${payment.amount}',
          adjustments: '${payment.adjustments}',
        },
      },
    },
    {
      id: 'result_posting_outcome',
      type: 'ExecutionResult',
      name: 'Posting Result',
      x: 1100,
      y: 250,
      metadata: {
        resultId: 'result_posting_001',
        status: 'success',
        output: {
          posted_amount: 1250.75,
          adjustments: -50.25,
          net_payment: 1200.5,
        },
      },
    },
  ],
  connections: [
    { source: 'wf_def_payment_posting', target: 'signal_era_received' },
    { source: 'signal_era_received', target: 'task_auto_post_payment' },
    { source: 'signal_era_received', target: 'task_denial_handling' },
    { source: 'task_auto_post_payment', target: 'timeline_payment_history' },
    { source: 'task_denial_handling', target: 'escalation_underpayment' },
    { source: 'timeline_payment_history', target: 'result_posting_outcome' },
  ],
};

/**
 * Example 5: Denial Management & Appeals
 */
export const DENIAL_MANAGEMENT_WORKFLOW: WorkflowMetadata = {
  id: 'rcm_denial_management_005',
  name: 'Denial Management & Appeals',
  description:
    'Systematic denial root cause analysis, appeal generation, and resubmission tracking',
  version: '2.5.0',
  status: 'active',
  createdAt: '2025-05-15T08:00:00Z',
  createdBy: 'system',
  updatedAt: '2025-09-30T10:00:00Z',
  updatedBy: 'denial_manager',
  complianceFlags: ['HIPAA', 'TIMELY_FILING', 'APPEALS_COMPLIANCE'],
  tags: ['rcm', 'denials', 'appeals', 'ar-recovery'],
  tenant: 'healthcare_org',
  nodes: [
    {
      id: 'wf_def_denials',
      type: 'WorkflowDefinition',
      name: 'Denial Management',
      x: 100,
      y: 300,
      metadata: {
        name: 'Denial Management & Appeals',
        version: '2.5.0',
        startAt: 'identify_denial',
      },
    },
    {
      id: 'task_denial_classification',
      type: 'Task',
      name: 'Classify Denial Reason',
      x: 350,
      y: 300,
      metadata: {
        taskId: 'task_classify_denial_001',
        taskType: 'automated',
        priority: 'high',
        categories: [
          'medical_necessity',
          'coding_error',
          'authorization_missing',
          'timely_filing',
          'duplicate_claim',
        ],
      },
    },
    {
      id: 'ai_appeal_generator',
      type: 'OptimizationRecommendation',
      name: 'AI Appeal Letter Generator',
      x: 600,
      y: 250,
      metadata: {
        recommendationId: 'ai_appeal_001',
        recommendationType: 'ai_appeal_generation',
        description: 'Generate appeal letters with medical necessity justification',
        impact: '60% faster appeals, 35% higher overturn rate',
        confidence: 88,
      },
    },
    {
      id: 'task_appeal_submission',
      type: 'Task',
      name: 'Submit Appeal',
      x: 850,
      y: 300,
      metadata: {
        taskId: 'task_appeal_001',
        taskType: 'manual',
        assignee: 'role_appeal_specialist',
        priority: 'critical',
        dueDate: '+30d',
      },
    },
    {
      id: 'escalation_high_value_denial',
      type: 'Escalation',
      name: 'Escalate High-Value Denials',
      x: 600,
      y: 450,
      metadata: {
        escalationId: 'esc_high_value_001',
        severity: 'critical',
        reason: 'Denial amount exceeds $10,000 threshold',
        escalateTo: 'role_revenue_director',
        deadline: '+2d',
      },
    },
  ],
  connections: [
    { source: 'wf_def_denials', target: 'task_denial_classification' },
    { source: 'task_denial_classification', target: 'ai_appeal_generator' },
    { source: 'ai_appeal_generator', target: 'task_appeal_submission' },
    { source: 'task_denial_classification', target: 'escalation_high_value_denial' },
  ],
};

/**
 * Example 6: Patient Collections & Payment Plans
 */
export const PATIENT_COLLECTIONS_WORKFLOW: WorkflowMetadata = {
  id: 'rcm_patient_collections_006',
  name: 'Patient Collections & Payment Plans',
  description:
    'Patient-friendly collections workflow with payment plan options, financial assistance screening, and compliance',
  version: '1.3.0',
  status: 'active',
  createdAt: '2025-04-01T08:00:00Z',
  createdBy: 'system',
  updatedAt: '2025-09-30T10:00:00Z',
  updatedBy: 'collections_manager',
  complianceFlags: ['HIPAA', 'FDCPA', 'STATE_COLLECTIONS_LAWS'],
  tags: ['rcm', 'patient-collections', 'payment-plans', 'financial-assistance'],
  tenant: 'healthcare_org',
  nodes: [
    {
      id: 'wf_def_collections',
      type: 'WorkflowDefinition',
      name: 'Patient Collections',
      x: 100,
      y: 300,
      metadata: {
        name: 'Patient Collections & Payment Plans',
        version: '1.3.0',
        startAt: 'calculate_patient_responsibility',
      },
    },
    {
      id: 'task_calculate_responsibility',
      type: 'Task',
      name: 'Calculate Patient Responsibility',
      x: 350,
      y: 300,
      metadata: {
        taskId: 'task_calc_patient_resp_001',
        taskType: 'automated',
        calculation: 'total_charges - insurance_payments - adjustments',
      },
    },
    {
      id: 'policy_collections_compliance',
      type: 'Policy',
      name: 'FDCPA Compliance Policy',
      x: 600,
      y: 250,
      metadata: {
        policyId: 'policy_fdcpa_001',
        policyType: 'compliance',
        rules: {
          no_calls_before: '08:00',
          no_calls_after: '21:00',
          max_calls_per_day: 3,
          require_opt_in_for_sms: true,
          cease_and_desist_honored: true,
        },
        severity: 'critical',
      },
    },
    {
      id: 'task_payment_plan_setup',
      type: 'Task',
      name: 'Setup Payment Plan',
      x: 850,
      y: 300,
      metadata: {
        taskId: 'task_payment_plan_001',
        taskType: 'manual',
        assignee: 'role_financial_counselor',
        priority: 'medium',
      },
    },
    {
      id: 'integration_payment_portal',
      type: 'Integration',
      name: 'Patient Payment Portal',
      x: 1100,
      y: 300,
      metadata: {
        integrationId: 'int_payment_portal_001',
        provider: 'custom',
        name: 'Patient Online Payment System',
        endpoint: 'https://payments.hospital.org/api',
      },
    },
  ],
  connections: [
    { source: 'wf_def_collections', target: 'task_calculate_responsibility' },
    { source: 'task_calculate_responsibility', target: 'policy_collections_compliance' },
    { source: 'policy_collections_compliance', target: 'task_payment_plan_setup' },
    { source: 'task_payment_plan_setup', target: 'integration_payment_portal' },
  ],
};

/**
 * All Healthcare RCM Example Workflows
 */
export const HEALTHCARE_RCM_EXAMPLES: WorkflowMetadata[] = [
  PATIENT_REGISTRATION_WORKFLOW,
  MEDICAL_CODING_WORKFLOW,
  CLAIMS_SUBMISSION_WORKFLOW,
  DENIAL_MANAGEMENT_WORKFLOW,
  PATIENT_COLLECTIONS_WORKFLOW,
];

/**
 * Load example workflows into the designer
 */
export function loadRCMExamples(): WorkflowMetadata[] {
  return HEALTHCARE_RCM_EXAMPLES;
}
